import { drawMaster, findVisibleBox, reachesEdges, toGrid, toSource } from "./FrameBuilder.helpers"
import { SourceBackground } from "./SourceBackground"
import { SourceError } from "./SourceLoader.errors"
import { CursorGrid } from "../../shared/services"

import type { Color } from "../types/Color"
import type { Drawing, Frame } from "../types/Frame"
import type { Framing } from "../types/Framing"
import type { ResolvedResampling } from "../types/Resampling"

// A part of the source image, in its whole pixels.
export interface Crop {
  x: number
  y: number
  width: number
  height: number
}

// Sources this small are pixel art, which the grid always scales up.
const CRISP_MAX_SIDE = 64

// Reduces one decoded image to a grid-sized frame. Throws a SourceError with a message safe to show.
export class FrameBuilder {
  // Crisp keeps pixel art blocky; anything bigger is smoothed. An SVG is never pixel art, however
  // small its declared size.
  static autoResampling(width: number, height: number, type: string): ResolvedResampling {
    return type !== "image/svg+xml" && Math.max(width, height) <= CRISP_MAX_SIDE
      ? "crisp"
      : "smooth"
  }

  static async build(
    image: HTMLImageElement | ImageBitmap,
    width: number,
    height: number,
    type: string,
    name: string,
  ): Promise<Frame> {
    const auto = this.autoResampling(width, height, type)
    const { canvas, context, area } = await drawMaster(image, width, height, type, "fit", auto)
    const pixels = this.#readMaster(context)
    const sourceBackground = SourceBackground.find(pixels, area)
    const whole = { x: 0, y: 0, width, height }
    const imageBox = toGrid(whole, whole)
    // Found once, so every resampling keeps the same part of the image.
    const artCrop = await this.#findArtCrop(image, whole, type, auto, sourceBackground, pixels)

    const draw = async (resampling: ResolvedResampling, framing: Framing, crop?: Crop) =>
      this.#readMaster(
        (await drawMaster(image, width, height, type, framing, resampling, crop)).context,
      )
    // Drawn now, while the full-size image is at hand: the fitted master is too small to fill,
    // trim or resample from.
    const drawWith = async (resampling: ResolvedResampling): Promise<Drawing> => {
      const fitted = resampling === auto ? pixels : await draw(resampling, "fit")
      const filledPixels = width === height ? fitted : await draw(resampling, "fill")
      const drawing = { pixels: fitted, filledPixels }
      if (!artCrop || artCrop === whole) {
        return { ...drawing, art: artCrop && { box: imageBox, ...drawing } }
      }

      const artPixels = await draw(resampling, "fit", artCrop)
      const artFilledPixels =
        artCrop.width === artCrop.height ? artPixels : await draw(resampling, "fill", artCrop)
      return {
        ...drawing,
        art: { box: toGrid(artCrop, whole), pixels: artPixels, filledPixels: artFilledPixels },
      }
    }

    // The id is only a list key. Not crypto.randomUUID: that needs a secure context.
    const id = Math.random().toString(36).slice(2)
    return {
      id,
      name,
      pixels,
      url: canvas.toDataURL("image/png"),
      sourceBackground,
      imageBox,
      drawings: { smooth: await drawWith("smooth"), crisp: await drawWith("crisp") },
    }
  }

  // The part of the source a trim keeps: the whole image when no edge of it is empty, null when
  // nothing is visible.
  static async #findArtCrop(
    image: HTMLImageElement | ImageBitmap,
    whole: Crop,
    type: string,
    resampling: ResolvedResampling,
    background: Color | null,
    fitted: ImageData,
  ): Promise<Crop | null> {
    const findArt = (master: ImageData) =>
      findVisibleBox(background ? SourceBackground.cut(master, background) : master)
    const onGrid = findArt(fitted)
    if (!onGrid) return null
    if (reachesEdges(onGrid, toGrid(whole, whole))) return whole

    // A grid pixel spans many source pixels, and a faint one may hide in its neighbor, so the
    // edges are found again on a closer draw of the art and a pixel around it. One to one when
    // that fits the grid: upscaling would smooth the edges out past where they are.
    const near = toSource(
      {
        left: onGrid.left - 1,
        top: onGrid.top - 1,
        right: onGrid.right + 1,
        bottom: onGrid.bottom + 1,
      },
      whole,
    )
    const size = CursorGrid.SIZE
    const closer =
      Math.max(near.width, near.height) > size
        ? near
        : {
            x: near.x - Math.floor((size - near.width) / 2),
            y: near.y - Math.floor((size - near.height) / 2),
            width: size,
            height: size,
          }
    const { context } = await drawMaster(
      image,
      whole.width,
      whole.height,
      type,
      "fit",
      resampling,
      closer,
    )
    const exact = findArt(this.#readMaster(context))
    return exact ? toSource(exact, closer) : near
  }

  static #readMaster(context: CanvasRenderingContext2D) {
    try {
      return context.getImageData(0, 0, CursorGrid.SIZE, CursorGrid.SIZE)
    } catch {
      // Some browsers taint the canvas after drawing an SVG.
      throw new SourceError("This browser can't convert that SVG. Try a PNG instead.")
    }
  }
}
